import React, { useState } from 'react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { GripVertical, X, Plus, FileCheck } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';

export interface ChecklistItemData {
  id: string;
  title: string;
  description?: string;
  sortOrder: number;
  isRequired: boolean;
  requiresProof: boolean;
}

interface ChecklistBuilderProps {
  items: ChecklistItemData[];
  onChange: (items: ChecklistItemData[]) => void;
  maxItems?: number;
}

interface SortableItemProps {
  item: ChecklistItemData;
  onUpdate: (id: string, updates: Partial<ChecklistItemData>) => void;
  onRemove: (id: string) => void;
}

const SortableItem: React.FC<SortableItemProps> = ({
  item,
  onUpdate,
  onRemove,
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: item.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="bg-white border border-gray-300 rounded-lg p-4 mb-3"
    >
      <div className="flex items-start gap-3">
        {/* Drag Handle */}
        <div
          {...attributes}
          {...listeners}
          className="cursor-grab active:cursor-grabbing pt-2"
          aria-label="Drag to reorder"
        >
          <GripVertical className="w-5 h-5 text-gray-400" />
        </div>

        {/* Content */}
        <div className="flex-1 space-y-3">
          <Input
            value={item.title}
            onChange={(e) => onUpdate(item.id, { title: e.target.value })}
            placeholder="Checklist item title"
            className="font-medium"
            maxLength={500}
            required
          />
          <Textarea
            value={item.description || ''}
            onChange={(e) =>
              onUpdate(item.id, { description: e.target.value })
            }
            placeholder="Optional description"
            rows={2}
            maxLength={1000}
          />
          <div className="flex items-center gap-4">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={item.requiresProof}
                onChange={(e) =>
                  onUpdate(item.id, { requiresProof: e.target.checked })
                }
                className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
              />
              <span className="text-sm text-gray-700 flex items-center gap-1">
                <FileCheck className="w-4 h-4" />
                Requires proof upload
              </span>
            </label>
          </div>
        </div>

        {/* Remove Button */}
        <button
          onClick={() => onRemove(item.id)}
          className="text-red-600 hover:text-red-700 p-1"
          aria-label="Remove item"
        >
          <X className="w-5 h-5" />
        </button>
      </div>
    </div>
  );
};

export const ChecklistBuilder: React.FC<ChecklistBuilderProps> = ({
  items,
  onChange,
  maxItems = 50,
}) => {
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const oldIndex = items.findIndex((item) => item.id === active.id);
      const newIndex = items.findIndex((item) => item.id === over.id);

      const reorderedItems = arrayMove(items, oldIndex, newIndex).map(
        (item, index) => ({
          ...item,
          sortOrder: index,
        })
      );

      onChange(reorderedItems);
    }
  };

  const handleAddItem = () => {
    if (items.length >= maxItems) {
      alert(`Maximum ${maxItems} checklist items allowed`);
      return;
    }

    const newItem: ChecklistItemData = {
      id: `temp-${Date.now()}`,
      title: '',
      description: '',
      sortOrder: items.length,
      isRequired: true,
      requiresProof: false,
    };

    onChange([...items, newItem]);
  };

  const handleUpdateItem = (id: string, updates: Partial<ChecklistItemData>) => {
    onChange(
      items.map((item) =>
        item.id === id ? { ...item, ...updates } : item
      )
    );
  };

  const handleRemoveItem = (id: string) => {
    const updatedItems = items
      .filter((item) => item.id !== id)
      .map((item, index) => ({ ...item, sortOrder: index }));
    onChange(updatedItems);
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-sm font-medium text-gray-900">
            Checklist Items
          </h3>
          <p className="text-xs text-gray-500">
            {items.length} / {maxItems} items • Drag to reorder
          </p>
        </div>
      </div>

      {items.length === 0 ? (
        <div className="text-center py-8 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
          <p className="text-sm text-gray-600 mb-4">
            No checklist items yet. Add at least one item to continue.
          </p>
          <Button onClick={handleAddItem} variant="outline" size="sm">
            <Plus className="w-4 h-4 mr-2" />
            Add First Item
          </Button>
        </div>
      ) : (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext
            items={items.map((item) => item.id)}
            strategy={verticalListSortingStrategy}
          >
            {items.map((item) => (
              <SortableItem
                key={item.id}
                item={item}
                onUpdate={handleUpdateItem}
                onRemove={handleRemoveItem}
              />
            ))}
          </SortableContext>
        </DndContext>
      )}

      {items.length > 0 && items.length < maxItems && (
        <Button
          onClick={handleAddItem}
          variant="outline"
          className="w-full"
          size="sm"
        >
          <Plus className="w-4 h-4 mr-2" />
          Add Checklist Item
        </Button>
      )}
    </div>
  );
};
